import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, getAccessToken } from "@/api/client";
import { useStore } from "@/store/StoreContext";
import { FilterBar, type Filter } from "@/components/FilterBar";
import { usePerm } from "@/lib/perm";
import { Badge, Button, Input, Select } from "@/components/ui";
import type { Customer } from "@/api/types";
import { fmtDate } from "@/lib/dates";
import { statusTone } from "@/pages/CustomerPage";
import { Pager } from "@/components/Pager";

const PAGE = 50;

export function CustomersPage() {
  const { store } = useStore();
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [inactive, setInactive] = useState("");
  const [filters, setFilters] = useState<Filter[]>([]);
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [q, status, inactive, filters]);
  const navigate = useNavigate();
  const fileRef = useRef<HTMLInputElement>(null);
  const brandId = store?.brand_id;
  const params = new URLSearchParams({ brand_id: brandId ?? "", limit: String(PAGE + 1), offset: String(page * PAGE) });
  if (q) params.set("q", q); if (status) params.set("status", status); if (inactive) params.set("inactive_months", inactive); if (filters.length) params.set("filters", JSON.stringify(filters));
  const list = useQuery({ queryKey: ["customers-list", params.toString()], queryFn: () => api<Customer[]>(`/app/customers?${params}`), enabled: !!brandId });
  const importM = useMutation({
    mutationFn: async (file: File) => { const fd = new FormData(); fd.append("brand_id", brandId!); fd.append("file", file); return api<{ created: number; updated: number; skipped: number }>("/app/customers/import", { method: "POST", body: fd }); },
    onSuccess: (r) => { alertMsg(`${t("customers.imported")}: +${r.created} / ~${r.updated} / ✕${r.skipped}`); qc.invalidateQueries({ queryKey: ["customers-list"] }); },
  });
  const [msg, setMsg] = useState<string | null>(null);
  const alertMsg = (m: string) => { setMsg(m); setTimeout(() => setMsg(null), 6000); };
  const exportXlsx = async () => {
    const r = await fetch(`/api/v1/app/customers/export?brand_id=${brandId}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
    const blob = await r.blob(); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "customers.xlsx"; a.click();
  };
  const { can } = usePerm();
  if (!store || !brandId) return null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold mr-2">{t("nav.customers")}</h1>
        <Input className="!w-56" placeholder={t("appt.searchCustomer")} value={q} onChange={(e) => setQ(e.target.value)} />
        <Select className="!w-auto" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">{t("customers.anyStatus")}</option>{["guest", "new", "trial_booked", "trial_done", "confirmed", "blocked"].map((s) => <option key={s} value={s}>{t(`cstatus.${s}`)}</option>)}</Select>
        <Select className="!w-auto" value={inactive} onChange={(e) => setInactive(e.target.value)}><option value="">{t("customers.anyActivity")}</option>{[2, 3, 6, 12].map((m) => <option key={m} value={m}>{t("customers.inactiveFor", { months: m })}</option>)}</Select>
        <div className="ml-auto flex gap-2">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={(e) => e.target.files?.[0] && importM.mutate(e.target.files[0])} />
          <Button variant="ghost" onClick={() => fileRef.current?.click()}>{t("customers.import")}</Button>
          <Button variant="ghost" onClick={exportXlsx}>{t("customers.export")}</Button>
          <Button disabled={!can("customers", "edit")} title={!can("customers", "edit") ? t("perm.noEdit") : undefined} onClick={() => navigate("/app/customers/new")}>{t("customers.new")}</Button>
        </div>
      </div>
      <FilterBar brandId={brandId} storeId={store.id} entity="customers" filters={filters} onChange={setFilters} />
      {msg && <p className="rounded-lg bg-ok-soft p-2 text-sm text-ok">{msg}</p>}
      <table className="w-full text-sm bg-surface rounded-xl border overflow-hidden">
        <thead className="bg-surface-2 text-left"><tr><th className="p-2">{t("customers.name")}</th><th className="p-2">{t("customer.phone")}</th><th className="p-2">Email</th><th className="p-2">{t("customers.status")}</th><th className="p-2">{t("customers.noShows")}</th><th className="p-2">{t("customers.lastVisit")}</th><th className="p-2">{t("treat.nextShort")}</th></tr></thead>
        <tbody>{(list.data ?? []).slice(0, PAGE).map((c) => (
          <tr key={c.id} className="border-t hover:bg-surface-2 cursor-pointer" onClick={() => navigate(`/app/customers/${c.id}`)}>
            <td className="p-2 font-medium">{c.vip && <span className="text-amber-500" title="VIP">★ </span>}{c.first_name} {c.last_name}</td><td className="p-2">{c.phone}</td><td className="p-2 text-muted">{c.email ?? ""}</td>
            <td className="p-2"><Badge tone={statusTone(c.status)}>{t(`cstatus.${c.status}`)}</Badge></td><td className="p-2">{c.no_show_count}</td>
            <td className="p-2 text-muted">{c.last_visit_at ? fmtDate(c.last_visit_at, i18n.language) : "—"}</td>
            <td className="p-2">{c.next_due_on ? <Badge tone={c.next_due_on < new Date().toISOString().slice(0, 10) ? "amber" : "blue"}>{fmtDate(c.next_due_on, i18n.language)}</Badge> : ""}</td>
          </tr>))}</tbody>
      </table>
      <Pager page={page} pageSize={PAGE} count={list.data?.length ?? 0} onPage={setPage} />
    </div>
  );
}

